/**
 * Issue #767, ruling 1 — the squad average.
 *
 * `Σ total_runs / Σ (innings − not_outs)`, computed CLIENT-SIDE from N ×
 * `GET /stats/summary?player_id=` reads (no endpoint — brief-767.md
 * ruling 1; Martin's M-05 option (a), `decisions-2026-09-19.md:160`). The
 * API's own `avg` zero-fills whenever the divisor is absent
 * (`api/src/routes/statsCompute.ts:55`) and is never read here — the same
 * "read the counts, not the pre-divided figure" rule `lib/recordFigures.js`
 * already applies to the record screens.
 *
 * Split into a pure aggregator (`aggregateSquadAverage`, unit-testable with
 * no fetch at all) and a fetch orchestrator (`fetchPlayerSummaries`,
 * bounded concurrency) for the same reason `lib/recordFigures.js` and
 * `lib/recordGate.js` stay plain functions of their inputs: the ARITHMETIC
 * and the PARTIAL-FAILURE rule (S9) are what a test should pin cheaply and
 * exactly; the network call itself is a thin wrapper around `api.get`.
 */
import api from "./api";

/**
 * Run `worker` over `items`, at most `limit` in flight at once, preserving
 * input order in the returned array. Traps' own warning (brief-767.md):
 * the journey suite runs one worker against one local D1 process and
 * saturates under load, so this is deliberately bounded rather than
 * `Promise.all`ing every read at once.
 *
 * @template T, R
 * @param {T[]} items
 * @param {number} limit
 * @param {(item: T, index: number) => Promise<R>} worker
 * @returns {Promise<R[]>}
 */
export async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let next = 0;
  const lane = async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await worker(items[i], i);
    }
  };
  const lanes = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, lane);
  await Promise.all(lanes);
  return results;
}

/**
 * One player's `GET /stats/summary?player_id=` read, never throwing: a
 * failure is reported as `{ ok: false }` rather than rejecting, so one bad
 * read cannot abort the other N-1 lanes still in flight.
 */
async function readOneSummary(playerId) {
  try {
    const res = await api.get(`/stats/summary?player_id=${encodeURIComponent(playerId)}`);
    return { ok: true, playerId, data: res.data || {} };
  } catch (e) {
    return { ok: false, playerId, data: null };
  }
}

/**
 * Read every linked player's summary, 4 at a time by default (brief-767.md
 * "bounded concurrency (4 at a time is fine)").
 *
 * @param {string[]} playerIds
 * @param {{ concurrency?: number }} [options]
 * @returns {Promise<Array<{ ok: boolean, playerId: string, data: object|null }>>}
 */
export function fetchPlayerSummaries(playerIds, { concurrency = 4 } = {}) {
  return mapWithConcurrency(playerIds || [], concurrency, readOneSummary);
}

/**
 * The pure decision (ruling 1/2, S9): given every player's summary read
 * (or failure), what does the squad average card show?
 *
 * - Any read failed -> `{ status: "failed" }`. Never a partial figure
 *   labelled as the squad's (#706's rule, S9) — a sum over 13 of 14 players
 *   is not the squad's total, and showing it as one is the exact false
 *   total #706 exists to refuse.
 * - Every read landed, total dismissals is 0 -> `{ status: "no-dismissals",
 *   totalRuns }` — the card stays, with the em dash and "N runs · no
 *   dismissals yet" (ruling 2).
 * - Otherwise -> `{ status: "ok", value, totalRuns, totalDismissals,
 *   playerCount }`.
 *
 * @param {Array<{ ok: boolean, data: object|null }>} results
 */
export function aggregateSquadAverage(results) {
  const list = results || [];
  if (list.some((r) => !r || !r.ok)) {
    return { status: "failed", value: null, totalRuns: 0, totalDismissals: 0, playerCount: list.length };
  }

  let totalRuns = 0;
  let totalDismissals = 0;
  for (const r of list) {
    const d = r.data || {};
    const innings = Number(d.innings) || 0;
    const notOuts = Number(d.not_outs) || 0;
    totalRuns += Number(d.total_runs) || 0;
    totalDismissals += innings - notOuts;
  }

  if (totalDismissals <= 0) {
    return { status: "no-dismissals", value: null, totalRuns, totalDismissals: 0, playerCount: list.length };
  }

  const value = Math.round((totalRuns / totalDismissals) * 100) / 100;
  return { status: "ok", value, totalRuns, totalDismissals, playerCount: list.length };
}

/**
 * One player's ROW figures (ruling 3, issue #741 § 4) — "8 inns · avg 27.4
 * · SR 71", or, for a player who has never been dismissed, "1 inn · 34
 * runs · SR 92" plus the separate "Not out, so there is no average yet"
 * line.
 *
 * The average is derived from the SAME counts the squad aggregate uses
 * (`innings − not_outs`), never the API's zero-filled `avg` (ruling 3). The
 * strike rate stays on the row's own read of `summary.sr` directly (ruling
 * 1 bars a second endpoint or a per-match read here) — issue #981 also
 * sends `total_balls_faced` alongside it, but that field is not read here:
 * `summary.sr` is no longer zero-filled either (`null` with no base), so
 * this row already gets the honest value without needing the raw count.
 * D102: one decimal.
 *
 * @param {object} summary — one player's `/stats/summary` payload.
 * @returns {{ innings: number, dismissals: number, totalRuns: number,
 *   average: number|null, strikeRate: number|null, isNotOutOnly: boolean }}
 */
export function playerRowFigures(summary) {
  const d = summary || {};
  const innings = Number(d.innings) || 0;
  const notOuts = Number(d.not_outs) || 0;
  const totalRuns = Number(d.total_runs) || 0;
  const dismissals = innings - notOuts;
  const sr = d.sr == null ? null : Number(d.sr);

  return {
    innings,
    dismissals,
    totalRuns,
    average: dismissals > 0 ? Math.round((totalRuns / dismissals) * 100) / 100 : null,
    strikeRate: sr,
    isNotOutOnly: innings > 0 && dismissals <= 0,
  };
}

/** "1 inn" for exactly one, "N inns" otherwise — the drawing's own two
 * shapes (`Squad.dc.html:129,160`). */
export function inningsWord(innings) {
  return innings === 1 ? "1 inn" : `${innings} inns`;
}

/**
 * PR #920 review, F2 — the row's OWN status, from the wrapped per-player
 * read `CoachSquad.jsx`'s `summaries` state holds for one id: `null` while
 * the whole batch is still in flight (that state starts `null` and is set
 * once, in full, when `fetchPlayerSummaries` resolves — see that file), or
 * `{ ok, data }` once it has.
 *
 * THE BUG THIS REPLACES: the row used to call `playerRowFigures(summary?.
 * data)` directly, for all three shapes alike. `playerRowFigures(undefined)`
 * — correctly, for that function's OWN contract, which never claims to know
 * whether zero means "counted and it's zero" or "not counted yet" — returns
 * `innings: 0`, and the row printed "No innings yet" for a fact that was
 * actually UNKNOWN (still loading) or UNREADABLE (the read failed), never
 * for a genuine zero. This function is the one place that tells those two
 * apart from a real, successful zero-innings read, before `playerRowFigures`
 * ever runs — `CoachSquad.jsx` renders a skeleton for "loading", a short
 * failure note for "failed", and only reads `figures.innings === 0` as
 * "no innings yet" for "ok".
 *
 * @param {{ ok: boolean, data: object|null }|null} summary
 * @returns {{ status: "loading" } | { status: "failed" } | { status: "ok", figures: ReturnType<typeof playerRowFigures> }}
 */
export function playerRowStatus(summary) {
  if (!summary) return { status: "loading" };
  if (!summary.ok) return { status: "failed" };
  return { status: "ok", figures: playerRowFigures(summary.data) };
}
